import { ComboBox, FormGroup, Label } from "@xola/ui-kit";
import clsx from "clsx";
import { FieldProps, Field as FormikField, useFormikContext } from "formik";
import React from "react";
import { Field, FieldType, QuestionnairePricingStructure } from "types";
import { CustomQuestions, PredefinedQuestions, QuestionnaireModalType } from "@/models/Form/QuestionnaireModalType";

export const QuestionTypeField = ({ modalType }: { modalType: QuestionnaireModalType }) => {
    const { values } = useFormikContext<Field>();

    const isPredefined = PredefinedQuestions.some((info) => info.type === modalType);
    const isTextOrDropdownType = textOrDropDownTypes.has(modalType);
    const isDisabled = isPredefined && !isTextOrDropdownType;
    const isAddonField = modalType === QuestionnaireModalType.ADDON;

    const questionTypeOptions = getQuestionTypeOptions(isPredefined, isTextOrDropdownType, modalType);
    const questionTypeValue = questionTypeOptions.find((option) => option.value === modalType);

    if (!modalType || isDisabled) {
        return (
            <FormGroup>
                <Label>Question Type</Label>
                <ComboBox isDisabled options={questionTypeOptions} value={questionTypeValue} />
            </FormGroup>
        );
    }

    return (
        <>
            <FormGroup data-testid="question-type-field-group">
                <Label className={isAddonField ? "text-gray" : undefined}>Question Type</Label>
                <FormikField name="type">
                    {({ field: { value }, form }: FieldProps) => {
                        const handleChange = (option: any) => {
                            const info = CustomQuestions.find((item) => item.type === option.value);
                            if (info) {
                                form.setFieldValue("type", info.fieldType);
                                form.setFieldValue("options", []);

                                if (info.fieldType === FieldType.PICKUP) {
                                    form.setFieldValue("organizerOnly", true);
                                    form.setFieldValue("priceType", QuestionnairePricingStructure.PER_PERSON);
                                } else {
                                    form.setFieldValue("priceType", null);
                                }
                            }
                        };
                        const info = CustomQuestions.find((item) => item.fieldType === value);
                        const option = { label: info?.name, value: info?.type };

                        return <ComboBox options={questionTypeOptions} value={option} onChange={handleChange} />;
                    }}
                </FormikField>
            </FormGroup>
            {values.type === FieldType.SELECT && (
                <FormikField name="options">
                    {({ field: { value }, form }: FieldProps) => {
                        if (form.values.type !== FieldType.SELECT || isDisabled) {
                            return null;
                        }

                        const error = form.errors.options;
                        return (
                            <FormGroup data-testid="question-add-option-field-group">
                                <Label className="whitespace-nowrap">
                                    <span className={clsx(error && "!text-danger")}>Add Option </span>
                                    <span className="text-primary">(Type and Press Enter to add) </span>
                                    <span className={clsx(error && "!text-danger")}>{error} </span>
                                </Label>
                                <ComboBox
                                    isCreatable
                                    isMulti
                                    closeMenuOnSelect
                                    classNames={{
                                        option: () => "no-translate",
                                        multiValueLabel: () => "no-translate",
                                        multiValueRemove: () => "no-translate",
                                    }}
                                    value={(value ?? []).map((item: string) => ({ label: item, value: item }))}
                                    placeholder="Add option"
                                    isError={!!error}
                                    onChange={(options: []) => {
                                        form.setFieldValue(
                                            "options",
                                            options.map((option: any) => option.value),
                                        );
                                    }}
                                />
                            </FormGroup>
                        );
                    }}
                </FormikField>
            )}
        </>
    );
};

const textOrDropDownTypes = new Set([QuestionnaireModalType.PICKUP, QuestionnaireModalType.HOW_HEAR_US]);

const getQuestionTypeOptions = (
    isPredefined: boolean,
    isTextOrDropdownType: boolean,
    modalType: QuestionnaireModalType,
) => {
    if (isTextOrDropdownType) {
        let types = [QuestionnaireModalType.SHORT_TEXT, QuestionnaireModalType.DROPDOWN, QuestionnaireModalType.PICKUP];

        if (modalType === QuestionnaireModalType.PICKUP) {
            types = types.filter((type) => type !== QuestionnaireModalType.DROPDOWN);
        }

        if (modalType === QuestionnaireModalType.HOW_HEAR_US) {
            types = types.filter((type) => type !== QuestionnaireModalType.PICKUP);
        }

        return CustomQuestions.filter((info) => types.includes(info.type)).map((info) => ({
            label: info.name,
            value: info.type,
        }));
    }

    return (isPredefined ? PredefinedQuestions : CustomQuestions).map((info) => ({
        label: info.name,
        value: info.type,
    }));
};
